<template>
  <div class="pageRoot linkListPage">
    <div ref="pageCard" class="pageShell">
      <div ref="pageCardMain" class="pageShell__main">
        <div
          ref="scrollArea"
          class="pageShell__scroll"
          :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
        >
          <div
            class="listWorkspace"
            :class="{ 'listWorkspace--scrolled': controlBarScrolled }"
          >
            <section
              ref="filterAnchor"
              class="filterCard filterAnchor"
              :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
            >
              <unlock-mode-list-filter-panel
                v-bind="filterPanelProps"
                v-on="filterPanelListeners"
              />
            </section>

            <section
              ref="tableZone"
              class="tableCard tableZone"
              :class="{ 'tableCard--scrolled': controlBarScrolled }"
            >
              <div
                class="scrollControlBar"
                :class="{ 'scrollControlBar--scrolled': controlBarScrolled }"
              >
                <div class="scrollControlBar__lead">
                  <span class="scrollControlBar__label">共</span>
                  <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
                  <span class="scrollControlBar__label">条</span>
                </div>

                <div
                  v-if="!controlBarScrolled"
                  class="scrollControlBar__stage scrollControlBar__stage--full"
                >
                  <control-bar-display
                    layout="info"
                    :scope-filters="activeScopeFilters"
                    :has-active-scope-filters="hasActiveScopeFilters"
                    :scope-filter-tooltip="scopeFilterTooltip"
                    :selection-count="0"
                    @open-filter="openFilterDrawer"
                  />
                </div>

                <template v-else>
                  <div class="scrollControlBar__stage scrollControlBar__stage--left">
                    <control-bar-display
                      part="info"
                      layout="info"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :scope-filter-tooltip="scopeFilterTooltip"
                      :selection-count="0"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__navSlot">
                    <control-bar-nav
                      @scroll-top="scrollToTableTop"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__stage scrollControlBar__stage--right" />
                </template>

                <div class="scrollControlBar__page">
                  <template v-if="listTotalCount > 0">
                    <span>第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                  <template v-else>
                    <span class="scrollControlBar__emptyHint">暂无数据</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                </div>
              </div>

              <div v-if="!loading && tableData.length" class="tableSection">
                <el-table
                  ref="multipleTable"
                  class="linkTable linkTable--hybrid"
                  :data="tableData"
                  tooltip-effect="dark"
                  style="width: 100%"
                  row-key="id"
                >
                  <el-table-column
                    label="解锁方式名称"
                    prop="name"
                    align="center"
                    header-align="center"
                    min-width="180"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span class="nameCell">{{ scope.row.name || '—' }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="解锁方式"
                    prop="lockType"
                    align="center"
                    header-align="center"
                    min-width="140"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span
                        class="typeTag"
                        :class="'typeTag--' + getLockTypeVariant(scope.row.lockType)"
                      >
                        {{ scope.row.lockType || '—' }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="价格"
                    prop="price"
                    align="center"
                    header-align="center"
                    width="100"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span class="cellMuted">{{ formatPrice(scope.row.price) }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="操作"
                    label-class-name="linkListActionColumnHead"
                    align="center"
                    header-align="center"
                    width="120"
                    class-name="actionColumn"
                  >
                    <template slot="header">
                      <span class="linkListActionColumnHead__label">操作</span>
                    </template>
                    <template slot-scope="scope">
                      <div class="linkTableActionCell">
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-view"
                          @click.stop="lookInfo(scope.row.id)"
                        >
                          查看
                        </el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div v-else-if="!loading && !tableData.length" class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-unlock" /></div>
                <p class="tableEmpty__title">暂无解锁方式</p>
                <p class="tableEmpty__desc">调整筛选条件或新增解锁方式。</p>
                <el-button
                  type="primary"
                  size="small"
                  v-check-role
                  @click="dialogOpen()"
                >
                  新增解锁方式
                </el-button>
              </div>

              <div v-else class="tableSkeletonWrap">
                <el-skeleton :rows="8" animated />
              </div>
            </section>
          </div>
        </div>

        <div
          v-if="filterDrawerOpen"
          class="filterDrawerLayer"
          role="dialog"
          aria-modal="true"
          aria-label="筛选与操作"
        >
          <div
            class="filterDrawerPanel"
            :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }"
            :style="drawerPanelStyle"
          >
            <div class="filterDrawerPanel__header">
              <span class="filterDrawerPanel__title">筛选与操作</span>
              <div class="filterDrawerPanel__headerActions">
                <span v-if="activeFilterCount" class="filterDrawerPanel__chip">
                  已筛选 {{ activeFilterCount }} 项
                </span>
                <button
                  type="button"
                  class="filterDrawerPanel__closeBtn"
                  @click="closeFilterDrawer"
                >
                  收起
                </button>
              </div>
            </div>
            <div ref="drawerScroll" class="filterDrawerPanel__scroll">
              <unlock-mode-list-filter-panel
                v-bind="{ ...filterPanelProps, showPanelSummary: true }"
                v-on="filterPanelListeners"
              />
            </div>
          </div>
          <div
            class="filterDrawerBackdrop"
            aria-hidden="true"
            @click="closeFilterDrawer"
            @wheel.prevent
          />
        </div>
      </div>

      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第
          {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[25, 50, 100]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <el-dialog
      :title="
        nowStatus === 0
          ? '新建解锁方式'
          : nowStatus === 1
          ? '查看解锁方式'
          : '编辑解锁方式'
      "
      :visible.sync="dialogVisible"
      :before-close="handleClose"
      style="padding: 0 50px"
      width="40%"
    >
      <el-form
        ref="ruleForm"
        :model="ruleForm"
        :rules="rules"
        label-width="120px"
        size="mini"
        v-loading="dialogLoading"
        :disabled="nowStatus === 1"
      >
        <el-form-item label="解锁方式名称" prop="name">
          <el-input
            v-model="ruleForm.name"
            placeholder="解锁方式名称"
          ></el-input>
        </el-form-item>
        <el-form-item v-if="nowStatus === 1" label="Key" prop="key">
          <el-input v-model="ruleForm.key" placeholder="Key"></el-input>
        </el-form-item>
        <el-form-item label="解锁方式" prop="lockType">
          <el-select
            v-model="ruleForm.lockType"
            placeholder="请选择"
            style="width: 100%"
            @change="lockChange(ruleForm.lockType)"
          >
            <el-option
              v-for="item in typeOptions"
              :key="'dialog_' + item"
              :label="item"
              :value="item"
            >
            </el-option>
          </el-select>
        </el-form-item>
        <template v-if="pinShow || ruleForm.lockType == '拼图解锁'">
          <el-form-item label="列数" prop="cols">
            <el-input
              type="number"
              v-model.number="ruleForm.cols"
              placeholder="列数"
              max="20"
              min="4"
            ></el-input>
          </el-form-item>
          <el-form-item label="行数" prop="rows">
            <el-input
              type="number"
              v-model.number="ruleForm.rows"
              placeholder="行数"
              max="10"
              min="3"
            ></el-input>
          </el-form-item>

          <el-form-item label="生命值" prop="health">
            <el-input
              type="number"
              v-model="ruleForm.health"
              placeholder="生命值"
            ></el-input>
          </el-form-item>
          <el-form-item label="提示道具" prop="hint">
            <el-input
              type="number"
              v-model="ruleForm.hint"
              placeholder="提示道具"
            ></el-input>
          </el-form-item>
          <el-form-item label="生命道具" prop="live">
            <el-input
              type="number"
              v-model="ruleForm.live"
              placeholder="生命道具"
            ></el-input>
          </el-form-item>
          <el-form-item label="奖励体力数" prop="vitality">
            <el-input
              type="number"
              v-model="ruleForm.vitality"
              placeholder="奖励体力数"
            ></el-input>
          </el-form-item>
        </template>
        <el-form-item
          v-if="priceShow && ruleForm.lockType !== '拼图解锁'"
          label="价格"
          prop="price"
        >
          <el-input v-model="ruleForm.price" placeholder="价格"></el-input>
        </el-form-item>

        <el-form-item label="备注" prop="message">
          <el-input
            v-model="ruleForm.message"
            type="textarea"
            :rows="2"
            placeholder="备注信息"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <div class="footerInfo" v-if="nowStatus === 1">
          <div></div>
          <div>
            <el-button @click="handleClose" size="mini">取 消</el-button>
            <el-button
              type="primary"
              @click="changeEdit()"
              size="mini"
              v-check-role
              >编 辑</el-button
            >
          </div>
        </div>
        <div v-else>
          <el-button @click="handleClose" size="mini">取 消</el-button>
          <el-button type="primary" size="mini" @click="submit">保 存</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  createLockList,
  editLockList,
  getLockListById,
  getLockList,
} from '../../../api/unlockMode'
import UnlockModeListFilterPanel from './UnlockModeListFilterPanel.vue'
import ControlBarDisplay from '../../resourceManage/genericResourceManage/ControlBarDisplay.vue'
import ControlBarNav from '../../resourceManage/genericResourceManage/ControlBarNav.vue'
import linkListScrollControlMixin from '../../resourceManage/genericResourceManage/linkListScrollControlMixin'
import { getLockTypeVariant } from '../unlockListHelpers'

export default {
  name: 'UnlockModeIndex',
  components: {
    UnlockModeListFilterPanel,
    ControlBarDisplay,
    ControlBarNav,
  },
  mixins: [linkListScrollControlMixin],
  data() {
    return {
      name: '',
      lockType: '',
      tableData: [],
      currentPage: 1,
      currentPageSize: 25,
      total: 0,
      loading: false,
      typeOptions: [
        '金币解锁',
        '免费解锁',
        '神秘壁纸',
        '拼图解锁',
        '视频解锁',
        '内购解锁',
        '代币解锁',
      ],
      dialogVisible: false,
      ruleForm: {
        name: '',
        lockType: '',
        price: '',
        message: '',
        key: '',
        rows: '',
        cols: '',
        health: '',
        hint: '',
        live: '',
        vitality: '',
      },
      rules: {
        name: [
          { required: true, message: '解锁方式名称不能为空', trigger: 'blur' },
        ],
        lockType: [
          {
            required: true,
            message: '解锁方式不能为空',
            trigger: 'change',
          },
        ],
        rows: [
          { required: true, message: '行数不能为空', trigger: 'blur' },
          {
            type: 'number',
            min: 4,
            max: 20,
            message: '行数的范围4~20',
            trigger: 'blur',
          },
        ],
        cols: [
          { required: true, message: '列数不能为空', trigger: 'blur' },
          {
            type: 'number',
            min: 3,
            max: 10,
            message: '列数的范围3~10',
            trigger: 'blur',
          },
        ],
        health: [
          { required: true, message: '生命值不能为空', trigger: 'blur' },
        ],
        hint: [
          { required: true, message: '提示道具不能为空', trigger: 'blur' },
        ],
        live: [
          { required: true, message: '生命道具不能为空', trigger: 'blur' },
        ],
        vitality: [
          { required: true, message: '奖励体力数不能为空', trigger: 'blur' },
        ],
        price: [{ required: true, message: '价格不能为空', trigger: 'blur' }],
      },
      nowStatus: 0,
      dialogLoading: false,
      priceShow: true,
      pinShow: false,
      id: '',
    }
  },
  computed: {
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    listRangeStart() {
      if (this.listTotalCount === 0) return 0
      return (this.currentPage - 1) * this.currentPageSize + 1
    },
    listRangeEnd() {
      if (this.listTotalCount === 0) return 0
      return Math.min(this.currentPage * this.currentPageSize, this.listTotalCount)
    },
    totalPages() {
      if (this.listTotalCount === 0) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
    activeFilterCount() {
      return [this.name, this.lockType].filter(
        (value) => value !== '' && value !== null && value !== undefined
      ).length
    },
    activeScopeFilters() {
      const chips = []
      if (String(this.name || '').trim()) {
        chips.push({
          key: 'name',
          label: '方式名称',
          value: this.name.trim(),
        })
      }
      if (String(this.lockType || '').trim()) {
        chips.push({
          key: 'lockType',
          label: '解锁方式',
          value: this.lockType.trim(),
        })
      }
      return chips
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0
    },
    scopeFilterTooltip() {
      return this.activeScopeFilters
        .map((item) => `${item.label}: ${item.value}`)
        .join(' · ')
    },
    filterPanelProps() {
      return {
        name: this.name,
        lockType: this.lockType,
        activeFilterCount: this.activeFilterCount,
        lockTypeOptions: this.typeOptions,
        panelTotalCount: this.listTotalCount,
      }
    },
    filterPanelListeners() {
      return {
        search: this.search,
        'go-create': () => this.dialogOpen(),
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetAndSearch,
        'update:name': (value) => {
          this.name = value
        },
        'update:lockType': (value) => {
          this.lockType = value
        },
      }
    },
  },
  watch: {
    loading(value) {
      if (!value) {
        this.onLinkListDataRendered()
      } else {
        this.$nextTick(() => {
          this.measureTableZoneWidth()
        })
      }
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.scheduleTableLayout()
      })
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.syncDrawerLayout()
        })
      }
    },
  },
  created() {
    this.bindLinkListScrollControl()
    this.getList()
  },
  methods: {
    getLockTypeVariant,
    formatPrice(price) {
      if (price === '' || price == null) return '—'
      return String(price)
    },
    getList() {
      this.loading = true
      getLockList({
        pageNum: this.currentPage,
        pageSize: this.currentPageSize,
        name: this.name,
        lockType: this.lockType,
      })
        .then((res) => {
          if (res.success) {
            this.total = res.total
            this.tableData = res.data || []
          }
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.measureTableZoneWidth()
            this.scheduleTableLayout()
          })
        })
    },
    search() {
      this.currentPage = 1
      this.getList()
    },
    resetFilters() {
      this.name = ''
      this.lockType = ''
    },
    resetAndSearch() {
      this.resetFilters()
      this.search()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => {
        this.getList()
      })
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    lookInfo(id) {
      this.id = id
      this.dialogOpen(id)
    },
    handleClose() {
      this.ruleForm = {
        name: '',
        lockType: '',
        price: '',
        message: '',
        key: '',
        rows: '',
        cols: '',
        health: '',
        hint: '',
        live: '',
        vitality: '',
      }
      this.priceShow = true
      this.pinShow = false
      this.id = ''
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.clearValidate()
      }
      this.dialogVisible = false
    },
    dialogOpen(id = '') {
      if (id) {
        this.nowStatus = 1
        this.dialogLoading = true
        getLockListById({ id })
          .then((res) => {
            if (res.success) {
              this.ruleForm = res.data
              this.lockChange(this.ruleForm.lockType)
            }
          })
          .finally(() => {
            this.dialogLoading = false
          })
      } else {
        this.nowStatus = 0
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.ruleForm) {
          this.$refs.ruleForm.clearValidate()
        }
      })
    },
    changeEdit() {
      this.nowStatus = 2
    },
    lockChange(value) {
      if (value === '拼图解锁') {
        this.pinShow = true
        this.priceShow = false
        this.ruleForm.price = 0
      } else if (value === '免费解锁') {
        this.priceShow = false
        this.pinShow = false
        this.ruleForm.price = 0
      } else {
        this.pinShow = false
        this.priceShow = true
      }
    },
    submit() {
      this.$refs.ruleForm.validate((valid) => {
        if (!valid) {
          this.$message.info('您还有表单没填完整！')
          return false
        }
        if (this.nowStatus === 0) {
          createLockList(this.ruleForm).then((res) => {
            if (res.success) {
              this.$message.success('新建成功')
              this.getList()
              this.handleClose()
            }
          })
          return
        }
        const list = { ...this.ruleForm, id: this.id }
        editLockList(list).then((res) => {
          if (res.success) {
            this.$message.success('编辑成功')
            this.getList()
            this.handleClose()
          }
        })
      })
    },
  },
}
</script>

<style scoped>
.cellMuted {
  color: var(--link-ink-secondary);
}

.footerInfo {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
